import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { api, mediaUrl } from "@/lib/api";
import { trackEventOnce } from "@/lib/analytics";
import Seo, { canonicalBase } from "@/components/site/Seo";
import ArticleView from "@/components/site/ArticleView";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Articolo() {
  const { slug } = useParams();
  const { data, error, isLoading } = useSWR(`/diario/articles/${slug}`, fetcher, { revalidateOnFocus: false });
  const article = data?.article;
  // dateModified segue solo le modifiche sostanziali al contenuto, non ogni salvataggio.
  const modified = article?.content_updated_at || article?.published_at || article?.updated_at;

  useEffect(() => {
    if (!article) return;
    trackEventOnce(`article:${article.slug}`, "article_view", {
      article_slug: article.slug,
      article_category: article.category_name || "",
    });
    const base = canonicalBase();
    const url = article.canonical || `${base}/diario-dal-borgo/${article.slug}`;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-ld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: (article.seo_title || article.title).slice(0, 110),
      name: article.title,
      description: article.meta_description || article.excerpt,
      datePublished: article.published_at,
      dateModified: modified,
      inLanguage: "it-IT",
      articleSection: article.category_name,
      keywords: (article.tags || []).join(", "),
      image: article.hero_image ? [`${mediaUrl(article.hero_image)}?w=1200`] : undefined,
      author: { "@type": "Organization", name: "Osteria Aterrana", url: base },
      publisher: {
        "@type": "Organization",
        name: "Osteria Aterrana",
        url: base,
        logo: { "@type": "ImageObject", url: `${base}/logo-osteria-aterrana.png` },
      },
      url,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      isPartOf: { "@type": "Blog", name: "Diario dal Borgo", url: `${base}/diario-dal-borgo` },
    });
    document.getElementById("article-ld")?.remove();
    document.head.appendChild(ld);
    return () => document.getElementById("article-ld")?.remove();
  }, [article, modified]);

  if (isLoading) {
    return <div className="pt-40 pb-32 container-ed text-sm text-foreground/55">Carichiamo l'articolo…</div>;
  }
  if (error || !article) {
    return (
      <div className="pt-40 pb-32 bg-cream">
        <div className="container-ed max-w-xl">
          <h1 className="font-serif text-4xl text-wood">Articolo non trovato</h1>
          <Link to="/diario-dal-borgo" className="mt-8 inline-flex px-8 py-4 bg-sage text-cream text-[0.7rem] uppercase tracking-[0.2em]">
            Torna al diario
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={article.seo_title || `${article.title} · Osteria Aterrana`}
        description={article.meta_description || article.excerpt}
        path={`/diario-dal-borgo/${article.slug}`}
        type="article"
        image={article.hero_image ? `${mediaUrl(article.hero_image)}?w=1200` : undefined}
        publishedTime={article.published_at}
        modifiedTime={modified}
        section={article.category_name}
      />
      <ArticleView
        article={article}
        related={data.related || []}
        references={article.scientific_references || []}
      />
    </>
  );
}
